import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  onSnapshot,
  writeBatch,
  type DocumentData,
  type QueryConstraint,
  type UpdateData,
} from 'firebase/firestore';

import { getFirestore } from './firebase-service';

// Firestore caps a write batch at 500 operations.
const BATCH_LIMIT = 500;

/** A bulk write that failed after `written` docs had already been committed in earlier batches. */
export class BulkWriteError extends Error {
  constructor(public readonly written: number, total: number, cause: unknown) {
    super(`Bulk write failed after ${written} of ${total} docs`, { cause });
    this.name = 'BulkWriteError';
  }
}

interface FirestoreCrudConfig {
  collectionName: string;
  /** Suffix for the create/update/delete error messages, e.g. 'Module' → 'createModule error:' */
  entityName: string;
  /** Log tag for subscription errors, e.g. 'subscribeToModules' */
  subscribeName: string;
}

export function createFirestoreCrudService<TDoc, TInput>({ collectionName, entityName, subscribeName }: FirestoreCrudConfig) {
  const create = async (data: TInput): Promise<string> => {
    try {
      const firestore = getFirestore();
      const collectionRef = collection(firestore, collectionName);
      const docRef = await addDoc(collectionRef, {
        ...data,
        createdAt: new Date().toISOString(),
      });
      return docRef.id;
    } catch (error) {
      console.error(`create${entityName} error:`, error);
      throw error;
    }
  };

  const update = async (id: string, data: Partial<TInput>, userEmail: string): Promise<void> => {
    try {
      const firestore = getFirestore();
      const docRef = doc(firestore, collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString(),
        updatedBy: userEmail,
      } as DocumentData);
    } catch (error) {
      console.error(`update${entityName} error:`, error);
      throw error;
    }
  };

  /**
   * Applies the same update to many docs. Atomic per batch of 500; `data` may hold FieldValues such as arrayUnion.
   * Batches commit in order, so a failure leaves the earlier ones written; the thrown BulkWriteError says how many.
   */
  const updateMany = async (ids: string[], data: UpdateData<TInput>, userEmail: string): Promise<void> => {
    try {
      const firestore = getFirestore();
      const stamp = { updatedAt: new Date().toISOString(), updatedBy: userEmail };
      for (let start = 0; start < ids.length; start += BATCH_LIMIT) {
        const batch = writeBatch(firestore);
        for (const id of ids.slice(start, start + BATCH_LIMIT)) {
          batch.update(doc(firestore, collectionName, id), { ...(data as DocumentData), ...stamp });
        }
        try {
          await batch.commit();
        } catch (error) {
          throw new BulkWriteError(start, ids.length, error);
        }
      }
    } catch (error) {
      console.error(`update${entityName}s error:`, error);
      throw error;
    }
  };

  const remove = async (id: string): Promise<void> => {
    try {
      const firestore = getFirestore();
      const docRef = doc(firestore, collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`delete${entityName} error:`, error);
      throw error;
    }
  };

  const subscribe = (
    constraints: QueryConstraint[],
    onData: (docs: (TDoc & { id: string })[]) => void,
    onError: (error: Error) => void,
    errorContext = ''
  ): (() => void) => {
    const firestore = getFirestore();
    const q = query(collection(firestore, collectionName), ...constraints);

    // Docs the snapshot does not report as changed keep their previous object, so memoized rows skip re-rendering.
    let docsById = new Map<string, TDoc & { id: string }>();

    return onSnapshot(
      q,
      (snapshot) => {
        const changedIds = new Set(snapshot.docChanges().map((change) => change.doc.id));
        const docs = snapshot.docs.map(
          (snapshotDoc) =>
            (!changedIds.has(snapshotDoc.id) && docsById.get(snapshotDoc.id)) || {
              id: snapshotDoc.id,
              ...(snapshotDoc.data() as TDoc),
            }
        );
        docsById = new Map(docs.map((item) => [item.id, item]));
        onData(docs);
      },
      (error) => {
        console.error(`[${subscribeName}] Error${errorContext}:`, error);
        onError(error);
      }
    );
  };

  return { create, update, updateMany, remove, subscribe };
}
